import React, { Component } from "react";
import { observer } from "mobx-react";
import moment from "moment";
import { TextField, InputAdornment, Button, CircularProgress } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import { ProfileType } from "../../../types/enum";

import "./ConversationList.css";

@observer
class ConversationList extends Component {
    state = {
        searchQuery: "",
        activeFilter: "all"
    };

    handleSearchChange = (e) => {
        this.setState({ searchQuery: e.target.value });
    };

    handleFilterChange = (filter) => {
        this.setState({ activeFilter: filter });
    };

    getUnreadCount = (messages, currentProfileId) => {
        if (!messages) return 0;
        return messages.filter(msg => !msg.isRead && msg.profileId !== currentProfileId).length;
    };

    getLastMessage = (conversation) => {
        if (!conversation.messages || conversation.messages.length === 0) return "";
        const lastMsg = conversation.messages[conversation.messages.length - 1];
        const text = lastMsg.body?.replace(/<[^>]*>/g, '') || "";
        return text.length > 60 ? text.substring(0, 60) + "..." : text;
    };

    getParticipantDisplay = (conversation, currentProfileId) => {
        if (!conversation.conversationParticipants) return conversation.from || "Unknown";
        
        const otherParticipants = conversation.conversationParticipants
            .filter(p => p.profile?.id !== currentProfileId)
            .map(p => {
                const profile = p.profile;
                if (profile?.type === ProfileType.Agent.ordinal && profile?.profileName) {
                    return profile.profileName;
                }
                return `${profile?.firstName || ""} ${profile?.lastName || ""}`.trim();
            })
            .filter(name => name);
        
        if (otherParticipants.length === 0) return conversation.from || "You";
        if (otherParticipants.length === 1) return otherParticipants[0];
        return `${otherParticipants[0]} +${otherParticipants.length - 1}`;
    };

    getInitials = (name) => {
        if (!name) return "?";
        const parts = name.split(" ").filter(p => p);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    getProfileType = (conversation, currentProfileId) => {
        if (!conversation.conversationParticipants) return null;
        const otherParticipant = conversation.conversationParticipants
            .find(p => p.profile?.id !== currentProfileId);
        return otherParticipant?.profile?.type;
    };

    getProfileTypeBadge = (type) => {
        switch (type) {
            case ProfileType.Musician.ordinal:
                return { label: "Musician", className: "profile-badge-musician" };
            case ProfileType.Agent.ordinal:
                return { label: "Agent", className: "profile-badge-agent" };
            case ProfileType.Venue.ordinal:
                return { label: "Venue", className: "profile-badge-venue" };
            case ProfileType.Staff.ordinal:
                return { label: "Staff", className: "profile-badge-staff" };
            default:
                return null;
        }
    };

    formatTime = (date) => {
        const msgDate = moment(date);
        const today = moment().startOf('day');
        const yesterday = moment().subtract(1, 'day').startOf('day');
        
        if (msgDate.isSame(today, 'day')) {
            return msgDate.format('h:mm A');
        } else if (msgDate.isSame(yesterday, 'day')) {
            return 'Yesterday';
        } else if (msgDate.isAfter(moment().subtract(7, 'days'))) {
            return msgDate.format('ddd');
        } else {
            return msgDate.format('MMM D');
        }
    };

    filterConversations = () => {
        const { conversations, currentProfile } = this.props;
        const { searchQuery, activeFilter } = this.state;
        
        let filtered = [...conversations];
        
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(conv => 
                conv.subject?.toLowerCase().includes(query) ||
                conv.from?.toLowerCase().includes(query) ||
                this.getLastMessage(conv).toLowerCase().includes(query)
            );
        }
        
        if (activeFilter === "unread") {
            filtered = filtered.filter(conv => 
                this.getUnreadCount(conv.messages, currentProfile?.id) > 0
            );
        }
        
        return filtered;
    };

    render() {
        const { selectedId, isLoading, onSelectConversation, onNewMessage, currentProfile } = this.props;
        const { searchQuery, activeFilter } = this.state;
        const filteredConversations = this.filterConversations();

        return (
            <div className="conversation-list">
                <div className="conversation-list-header">
                    <div className="conversation-search">
                        <TextField
                            fullWidth
                            placeholder="Search messages..."
                            value={searchQuery}
                            onChange={this.handleSearchChange}
                            variant="outlined"
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start" style={{ background: 'transparent' }}>
                                        <SearchIcon style={{ color: '#999', fontSize: '1.25rem' }} />
                                    </InputAdornment>
                                ),
                                style: { 
                                    borderRadius: 4, 
                                    background: '#f5f5f5',
                                    paddingLeft: 8
                                }
                            }}
                        />
                    </div>
                </div>

                <div className="conversation-filter-tabs">
                    <button 
                        className={`filter-tab ${activeFilter === 'all' ? 'active' : ''}`}
                        onClick={() => this.handleFilterChange('all')}
                    >
                        All
                    </button>
                    <button 
                        className={`filter-tab ${activeFilter === 'unread' ? 'active' : ''}`}
                        onClick={() => this.handleFilterChange('unread')}
                    >
                        Unread
                    </button>
                </div>

                <div className="conversation-list-scroll">
                    {isLoading ? (
                        <div className="conversation-list-loading">
                            <CircularProgress size={32} style={{ color: '#FF0C9C' }} />
                        </div>
                    ) : filteredConversations.length === 0 ? (
                        <div className="conversation-list-empty">
                            <div className="empty-icon">💬</div>
                            <h3>No messages yet</h3>
                            <p>Start a conversation to connect with musicians, venues, and agents.</p>
                        </div>
                    ) : (
                        filteredConversations.map(conversation => {
                            const unreadCount = this.getUnreadCount(conversation.messages, currentProfile?.id);
                            const participantName = this.getParticipantDisplay(conversation, currentProfile?.id);
                            const profileType = this.getProfileType(conversation, currentProfile?.id);
                            const badge = this.getProfileTypeBadge(profileType);
                            
                            return (
                                <div
                                    key={conversation.id}
                                    className={`conversation-row ${selectedId === conversation.id ? 'active' : ''} ${unreadCount > 0 ? 'unread' : ''}`}
                                    onClick={() => onSelectConversation(conversation.id)}
                                >
                                    {unreadCount > 0 && <div className="unread-indicator" />}
                                    <div className="conversation-avatar">
                                        {this.getInitials(participantName)}
                                    </div>
                                    <div className="conversation-content">
                                        <div className="conversation-header">
                                            <span className="conversation-subject">{conversation.subject || "No Subject"}</span>
                                            <span className="conversation-time">{this.formatTime(conversation.createdAt)}</span>
                                        </div>
                                        <div className="conversation-participants">
                                            {participantName}
                                            {badge && (
                                                <span className={`profile-badge ${badge.className}`}>
                                                    {badge.label}
                                                </span>
                                            )}
                                        </div>
                                        <div className="conversation-preview">
                                            {this.getLastMessage(conversation)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                <button className="new-message-fab" onClick={onNewMessage}>
                    <AddIcon />
                </button>
            </div>
        );
    }
}

export default ConversationList;
